import { tokenStore } from "../api/axiosClient";

/**
 * RBAC permission helpers (frontend).
 *
 * The backend embeds one "permission" claim per granted permission in the
 * JWT access token (see JwtTokenGenerator on the server). We decode the
 * token client-side ONLY to drive UI affordances — hiding buttons, gating
 * routes, etc. This is purely cosmetic: the server independently enforces
 * every permission via [HasPermission(...)]. A user who tampers with their
 * token to reveal a hidden button still gets a 403 from the API.
 *
 * Never treat client-side permission checks as a security boundary.
 */

/**
 * Decode the payload of a JWT without verifying its signature.
 * (Signature verification is the server's job; we only need to READ claims.)
 * Returns null on any malformed input.
 */
function decodeJwtPayload(token) {
  if (!token || typeof token !== "string") return null;

  const parts = token.split(".");
  if (parts.length !== 3) return null;

  try {
    // JWT uses base64url; convert to standard base64 then atob.
    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    // Pad to a multiple of 4 so atob doesn't choke.
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
    const json = decodeURIComponent(
      atob(padded)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

/**
 * Pull the set of permission strings from the current access token.
 * The "permission" claim is an array when there are multiple permissions,
 * a string when there's exactly one, and absent when there are none —
 * we normalize all three into an array.
 */
export function getCurrentPermissions() {
  const token = tokenStore.getAccessToken();
  const payload = decodeJwtPayload(token);
  if (!payload) return [];

  const claim = payload.permission;
  if (Array.isArray(claim)) return claim;
  if (typeof claim === "string") return [claim];
  return [];
}

/** The current user's role name from the token (e.g. "SuperAdmin"). */
export function getCurrentRole() {
  const token = tokenStore.getAccessToken();
  const payload = decodeJwtPayload(token);
  // ASP.NET serializes ClaimTypes.Role as "role" in the JWT.
  return payload?.role ?? null;
}

/** True if the current token grants the given permission code. */
export function hasPermission(code) {
  if (!code) return false;
  return getCurrentPermissions().includes(code);
}

/** True if the current token grants AT LEAST ONE of the given codes. */
export function hasAnyPermission(codes = []) {
  if (!Array.isArray(codes) || codes.length === 0) return false;
  const owned = getCurrentPermissions();
  return codes.some((c) => owned.includes(c));
}

/** True if the current token grants ALL of the given codes. */
export function hasAllPermissions(codes = []) {
  if (!Array.isArray(codes) || codes.length === 0) return false;
  const owned = getCurrentPermissions();
  return codes.every((c) => owned.includes(c));
}